<!-- src/components/MessageContent.vue - OPRAVENÁ VERZE PRO STREAMING -->
<template>
  <div class="relative" @click="handleLinkClick">
    <!-- STREAMING MODE - zpracováváme markdown PRŮBĚŽNĚ -->
    <div
      v-if="isStreaming && !isLoading && !isError"
      class="prose prose-sm max-w-none overflow-x-auto break-words"
      :class="{ 'prose-invert': isUser }"
    >
      <span v-html="streamingFormattedText"></span
      ><span class="animate-pulse">▊</span>
    </div>

    <!-- FINÁLNÍ MODE - plný markdown po dokončení streamingu -->
    <div
      v-else-if="!isLoading && !isError"
      class="prose prose-sm max-w-none overflow-x-auto break-words"
      :class="{ 'prose-invert': isUser }"
    >
      <span v-html="formattedText"></span>
    </div>

    <!-- Loading state -->
    <span v-if="isLoading" class="italic text-gray-500">
      nahřívám odpověď… 🔥
    </span>

    <!-- Error state -->
    <span v-if="isError" class="text-red-600">
      Došlo k chybě, zkuste to znovu.
    </span>
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import MarkdownIt from "markdown-it";
import markdownItAttrs from "markdown-it-attrs";
import DOMPurify from "dompurify";

const props = defineProps({
  text: String,
  isUser: {
    type: Boolean,
    default: false,
  },
  isLoading: Boolean,
  isError: Boolean,
  isStreaming: Boolean,
});

const emit = defineEmits(['sendMessage']);

// Track previous text length for fade-in animation
const previousTextLength = ref(0);

// Reset when streaming starts/stops
watch(() => props.isStreaming, (isStreaming) => {
  if (isStreaming) {
    previousTextLength.value = 0;
  }
});

const md = new MarkdownIt({
  html: true,
  breaks: true,
  linkify: true,
  typographer: true,
}).use(markdownItAttrs, {
  // Povolené atributy pro obrázky
  allowedAttributes: ['class', 'style', 'width', 'height', 'alt', 'title']
});

// Computed property pro STREAMING formátovaný text - s word-by-word fade-in
const streamingFormattedText = computed(() => {
  if (!props.isStreaming || !props.text) return "";

  if (typeof props.text === "object") {
    return "Chyba: Neplatný formát textu";
  }

  try {
    const currentText = props.text;
    const currentLength = currentText.length;

    // JEDNODUCHÝ markdown processing pro streaming
    let cleanText = currentText
      .replace(/([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})\1+/g, "$1");

    // Split text into old and new parts
    const oldText = cleanText.substring(0, previousTextLength.value);
    const newText = cleanText.substring(previousTextLength.value);

    // Process old text (already rendered, no animation)
    let oldHtml = oldText ? md.render(oldText) : "";

    // Process new text and split by words for individual animations
    let newHtml = "";
    if (newText) {
      // Split new text into words while preserving spaces and punctuation
      const words = newText.split(/(\s+)/);

      // Wrap each word in a span with staggered animation delay
      const wrappedWords = words.map((word, index) => {
        if (!word.trim()) return word; // Keep spaces as-is

        // Calculate delay: each word appears with slight delay (50ms stagger)
        const delay = index * 50;
        return `<span class="streaming-word" style="animation-delay: ${delay}ms">${word}</span>`;
      }).join('');

      // Render markdown on the wrapped text
      newHtml = md.render(wrappedWords);
    }

    // Combine old (stable) and new (animated) HTML
    let html = oldHtml + newHtml;

    // Update previous length for next render
    // eslint-disable-next-line vue/no-side-effects-in-computed-properties
    previousTextLength.value = currentLength;

    // Základní email fix
    html = html.replace(/<a href="([^"]*@[^"]*)">/g, '<a href="mailto:$1">');
    html = html.replace(/mailto:mailto:/g, "mailto:");

    // Základní keyword highlighting
    const keywords = [
      "HOTJET",
      "Köhler",
      "tepelná čerpadla",
      "tepelné čerpadlo",
    ];
    keywords.forEach((keyword) => {
      const regex = new RegExp(`\\b(${keyword})\\b(?![^<]*>|[^<]*<\/a>)`, "gi");
      html = html.replace(
        regex,
        '<mark class="bg-red-600 text-white px-1 rounded">$1</mark>'
      );
    });

    // Rychlé emoji replacement - jen nejčastější
    html = html
      .replace(/:fire:/g, "🔥")
      .replace(/:check:/g, "✅")
      .replace(/:phone:/g, "📞")
      .replace(/:email:/g, "📧");

    // ZJEDNODUŠENÉ DOMPurify - rychlejší pro streaming
    const sanitized = DOMPurify.sanitize(html, {
      ALLOWED_TAGS: [
        "p",
        "br",
        "strong",
        "em",
        "ul",
        "ol",
        "li",
        "h1",
        "h2",
        "h3",
        "h4",
        "code",
        "a",
        "mark",
        "img",
        "span",
      ],
      ALLOWED_ATTR: ["href", "target", "rel", "class", "src", "alt", "width", "height", "style"],
      ADD_ATTRIBUTES: { a: { target: "_blank", rel: "noopener noreferrer" } },
    });

    return sanitized;
  } catch (error) {
    console.error("Error in streamingFormattedText:", error);
    return props.text; // Fallback na raw text
  }
});

// Computed property pro formátovaný text - POUZE když NENÍ streaming
const formattedText = computed(() => {
  // DŮLEŽITÉ: Pokud je streaming aktivní, neprocessujeme markdown
  if (props.isStreaming) {
    return props.text || "";
  }

  if (!props.text) return "";

  if (typeof props.text === "object") {
    console.error("Received object instead of string for text:", props.text);
    return "Chyba: Neplatný formát textu";
  }

  try {
    // NEJDŘÍV oprav zdvojené emaily v původním textu
    let cleanText = props.text
      // Oprav zdvojené emaily v původním textu
      .replace(/([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})\1+/g, "$1")
      // Oprav emaily s extra textem
      .replace(
        /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g,
        "$1"
      );

    let html = md.render(cleanText);

    // NOVÁ STRATEGIE: Kompletní přepis email odkazů
    // 1. Najdi všechny email adresy v HTML a oprav je jedním tahem
    html = html.replace(
      /<a[^>]*href="[^"]*"[^>]*>([^<]*?)([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})([^<]*?)<\/a>/g,
      (match, before, email, after) => {
        // Pokud before nebo after obsahuje email znovu, ignoruj je
        const cleanBefore = before.includes("@") ? "" : before;
        const cleanAfter = after.includes("@") ? "" : after;
        return `<a href="mailto:${email}">${cleanBefore}${email}${cleanAfter}</a>`;
      }
    );

    // 2. Oprav nesprávné odkazy bez mailto
    html = html.replace(/<a href="([^"]*@[^"]*)">/g, '<a href="mailto:$1">');

    // 3. Oprav duplicitní mailto:
    html = html.replace(/mailto:mailto:/g, "mailto:");

    // 4. Najdi samostatné email adresy a udělej z nich odkazy
    html = html.replace(
      /(?<!href="[^"]*|>)([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})(?![^<]*<\/a>)/g,
      '<a href="mailto:$1">$1</a>'
    );

    // 5. FINÁLNÍ čištění zdvojených emailů v HTML
    html = html.replace(
      /<a[^>]*href="mailto:([^"]+)"[^>]*>([^<]*)\1([^<]*)<\/a>/g,
      '<a href="mailto:$1">$1</a>'
    );

    // 6. Oprav případy kde je email v textu odkazu zdvojený
    html = html.replace(
      /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})\1+/g,
      "$1"
    );

    // Zvýraznění klíčových slov - POZOR na email adresy
    const keywords = [
      "HOTJET",
      "Köhler",
      "tepelná čerpadla",
      "tepelné čerpadlo",
      "vzduch-voda",
      "země-voda",
      "voda-voda",
    ];

    keywords.forEach((keyword) => {
      const regex = new RegExp(`\\b(${keyword})\\b(?![^<]*>|[^<]*<\/a>)`, "gi");
      html = html.replace(
        regex,
        '<mark class="bg-red-600 text-white px-1 rounded">$1</mark>'
      );
    });

    // Emoji replacement
    html = replaceEmojiShortcodes(html);

    const sanitized = DOMPurify.sanitize(html, {
      ALLOWED_TAGS: [
        "p",
        "br",
        "strong",
        "em",
        "ul",
        "ol",
        "li",
        "h1",
        "h2",
        "h3",
        "h4",
        "h5",
        "h6",
        "blockquote",
        "code",
        "pre",
        "a",
        "table",
        "thead",
        "tbody",
        "tr",
        "th",
        "td",
        "sup",
        "sub",
        "mark",
        "del",
        "ins",
        "span",
        "img",
      ],
      ALLOWED_ATTR: ["href", "target", "rel", "class", "style", "title", "src", "alt", "width", "height"],
      KEEP_CONTENT: true,
      ADD_ATTRIBUTES: {
        a: {
          target: "_blank",
          rel: "noopener noreferrer",
        },
      },
      ALLOWED_URI_REGEXP:
        /^(?:(?:https?|ftp|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i,
      SANITIZE_NAMED_PROPS: false,
      FIX_NESTED_LINKS: false,
    });

    return sanitized;
  } catch (error) {
    console.error("Error in formattedText:", error);
    return "Chyba při formátování textu";
  }
});

// Nahrazení emoji shortcodes skutečnými emoji
function replaceEmojiShortcodes(html) {
  const emojiMap = {
    ":fire:": "🔥",
    ":check:": "✅",
    ":x:": "❌",
    ":bulb:": "💡",
    ":money:": "💰",
    ":house:": "🏠",
    ":sun:": "☀️",
    ":snowflake:": "❄️",
    ":chart:": "📊",
    ":wrench:": "🔧",
    ":phone:": "📞",
    ":email:": "📧",
    ":rocket:": "🚀",
    ":heart:": "❤️",
    ":thumbsup:": "👍",
    ":warning:": "⚠️",
    ":info:": "ℹ️",
    ":question:": "❓",
    ":ok:": "👌",
    ":star:": "⭐",
    ":lightning:": "⚡",
    ":checkmark:": "✔️",
    ":euro:": "€",
    ":thermometer:": "🌡️",
    ":droplet:": "💧",
    ":wind:": "💨",
    ":earth:": "🌍",
    ":gear:": "⚙️",
    ":tools:": "🛠️",
    ":ruler:": "📏",
    ":handshake:": "🤝",
    ":calculator:": "🧮",
    ":calendar:": "📅",
    ":money_bag:": "💰",
    ":zap:": "⚡",
  };

  Object.entries(emojiMap).forEach(([shortcode, emoji]) => {
    const escapedShortcode = shortcode.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(escapedShortcode, "g");
    html = html.replace(regex, emoji);
  });

  return html;
}

// Handler pro kliknutí na produkt link
function handleLinkClick(event) {
  const target = event.target;

  // Zkontroluj, jestli se kliklo na link (a nebo strong uvnitř a)
  const link = target.tagName === 'A' ? target : target.closest('a');

  if (link && link.textContent) {
    const productName = link.textContent.trim();

    // Zkontroluj, jestli to vypadá jako produkt HOTJET
    // (obsahuje ZETX, WX, ONE, MINI nebo čísla s písmeny)
    const isProductLink = /\d+[A-Z]+|ZETX|WX|ONE|MINI/i.test(productName);

    if (isProductLink) {
      event.preventDefault();

      // Odeslat dotaz na detail produktu
      const detailQuery = `Více informací o ${productName}`;
      emit('sendMessage', detailQuery);
    }
  }
}
</script>

<style scoped>
/* Prose styly zůstávají stejné... */
.prose {
  max-width: none;
  color: inherit;
}

.prose :deep(p) {
  margin: 0.5em 0;
  line-height: 1.6;
}

.prose :deep(p:first-child) {
  margin-top: 0;
}

.prose :deep(p:last-child) {
  margin-bottom: 0;
}

/* Table wrapper for horizontal scrolling */
.prose :deep(table) {
  @apply w-full my-3 text-sm border-collapse rounded-lg;
  display: block;
  overflow-x: auto;
  table-layout: auto;
  max-width: 100%;
}

.prose :deep(th),
.prose :deep(td) {
  @apply border border-gray-300 px-2 py-1.5 text-left text-xs;
  vertical-align: top;
  word-wrap: break-word;
  white-space: nowrap;
}

.prose :deep(th) {
  @apply bg-gray-100 font-semibold text-gray-900;
}

.prose :deep(tr:nth-child(even)) {
  @apply bg-gray-50;
}

.prose :deep(td:nth-child(3)),
.prose :deep(td:nth-child(4)),
.prose :deep(th:nth-child(3)),
.prose :deep(th:nth-child(4)) {
  min-width: 120px;
  white-space: nowrap;
}

.prose :deep(td) {
  max-width: 200px;
  overflow-wrap: break-word;
}

@media (max-width: 768px) {
  .prose :deep(table) {
    font-size: 12px;
  }

  .prose :deep(th),
  .prose :deep(td) {
    padding: 6px 8px;
    white-space: nowrap;
  }
}

.prose :deep(ul) {
  @apply list-disc list-inside my-2 space-y-1;
}

.prose :deep(ol) {
  @apply list-decimal list-inside my-2 space-y-1;
}

.prose :deep(li) {
  @apply ml-2;
}

.prose :deep(li p) {
  @apply inline;
}

.prose :deep(h1),
.prose :deep(h2),
.prose :deep(h3),
.prose :deep(h4) {
  @apply font-bold mt-4 mb-2 text-gray-900;
}

.prose :deep(h1) {
  @apply text-xl;
}

.prose :deep(h2) {
  @apply text-lg;
}

.prose :deep(h3) {
  @apply text-base;
}

.prose :deep(code) {
  @apply bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono text-gray-800;
}

.prose :deep(pre) {
  @apply bg-gray-100 p-3 rounded-lg overflow-x-auto my-2;
}

.prose :deep(pre code) {
  @apply bg-transparent p-0;
}

.prose :deep(a) {
  @apply text-blue-600 underline hover:text-blue-700 transition-colors;
}

.prose :deep(strong) {
  @apply font-bold text-gray-900;
}

.prose :deep(em) {
  @apply italic;
}

/* Obrázky - pozicování a obtékání textu */
.prose :deep(img) {
  @apply rounded-lg shadow-md my-4 max-w-full h-auto;
}

/* Obrázek vlevo - text obtéká zprava */
.prose :deep(img.left) {
  float: left;
  margin-right: 1rem;
  margin-bottom: 1rem;
  max-width: 50%;
}

/* Obrázek vpravo - text obtéká zleva */
.prose :deep(img.right) {
  float: right;
  margin-left: 1rem;
  margin-bottom: 1rem;
  max-width: 50%;
}

/* Obrázek na střed - text nad i pod */
.prose :deep(img.center) {
  display: block;
  margin-left: auto;
  margin-right: auto;
  max-width: 80%;
}

/* Malý obrázek - inline s textem */
.prose :deep(img.small) {
  display: inline-block;
  max-width: 200px;
  margin: 0 0.5rem;
}

/* Velký obrázek - přes celou šířku */
.prose :deep(img.full) {
  display: block;
  width: 100%;
  max-width: 100%;
  margin: 1.5rem 0;
}

/* Clearfix pro float elementy */
.prose :deep(p:has(img.left)),
.prose :deep(p:has(img.right)) {
  overflow: auto;
}

/* Responzivní - na mobilu všechny obrázky na střed */
@media (max-width: 640px) {
  .prose :deep(img.left),
  .prose :deep(img.right) {
    float: none;
    display: block;
    margin-left: auto;
    margin-right: auto;
    max-width: 100%;
  }
}

.prose :deep(blockquote) {
  @apply border-l-4 border-gray-300 pl-4 my-2 italic text-gray-700;
}

.prose-invert :deep(code) {
  @apply bg-red-700 text-white;
}

.prose-invert :deep(a) {
  @apply text-blue-200 hover:text-blue-100;
}

.prose-invert :deep(strong) {
  @apply text-white;
}

.prose-invert :deep(h1),
.prose-invertyhazovalo :deep(h2),
.prose-invert :deep(h3),
.prose-invert :deep(h4) {
  @apply text-white;
}

.prose-invert :deep(th) {
  @apply bg-red-700 text-white;
}

.prose-invert :deep(tr:nth-child(even)) {
  @apply bg-red-600/20;
}

.prose-invert :deep(blockquote) {
  @apply border-red-300 text-red-100;
}

.prose :deep(hr) {
  @apply my-4 border-gray-300;
}

.prose :deep(mark) {
  @apply bg-yellow-200 text-gray-900 px-1 rounded;
}

.prose :deep(.emoji) {
  @apply inline-block text-lg align-middle;
}

/* Styling pro raw text během streamingu */
.whitespace-pre-wrap {
  white-space: pre-wrap;
  word-wrap: break-word;
  line-height: 1.6;
}

/* Word-by-word streaming animation - každé slovo se objeví postupně */
.prose :deep(.streaming-word) {
  display: inline-block;
  animation: wordFadeIn 0.4s ease-out;
  animation-fill-mode: forwards;
  opacity: 0;
}

@keyframes wordFadeIn {
  0% {
    opacity: 0;
    transform: translateY(2px);
    color: rgb(209, 213, 219); /* gray-300 */
  }
  100% {
    opacity: 1;
    transform: translateY(0);
    color: inherit;
  }
}

/* Dark mode variant */
.prose-invert :deep(.streaming-word) {
  animation: wordFadeInInvert 0.4s ease-out;
  animation-fill-mode: forwards;
  opacity: 0;
}

@keyframes wordFadeInInvert {
  0% {
    opacity: 0;
    transform: translateY(2px);
    color: rgb(229, 231, 235); /* gray-200 */
  }
  100% {
    opacity: 1;
    transform: translateY(0);
    color: white;
  }
}
</style>
